import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FileText, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import LoadingScreen from '../LoadingScreen';
import InvoiceInbox from '../InvoiceInbox';
import { pageCache } from '../../utils/pageCache';
import { fmtCents } from '../../utils/formatters';

const INVOICE_STATUS: Record<string, { label: string; cls: string }> = {
  committed: { label: 'Processed', cls: 'bg-[#2E6B4F]/10 text-[#2E6B4F]' },
  needs_review: { label: "Totals don't add up — held", cls: 'bg-[#B54A4A]/10 text-[#B54A4A]' },
  extraction_failed: { label: "Couldn't read", cls: 'bg-[#B54A4A]/10 text-[#B54A4A]' },
  no_file: { label: 'No file attached', cls: 'bg-[#B54A4A]/10 text-[#B54A4A]' },
  failed: { label: "Couldn't fetch, please upload it", cls: 'bg-[#B54A4A]/10 text-[#B54A4A]' },
};

/** v4 — supplier invoices: drop zone, confirm queue, invoice history.
 *  Moved from the Upload page (2026-09-24 five doors); same API calls and
 *  states. The drop zone gained keyboard access when it moved. */
export default function SupplierInvoices() {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [invoiceUploading, setInvoiceUploading] = useState(false);
  const [invoiceDragOver, setInvoiceDragOver] = useState(false);
  const [inboxRefresh, setInboxRefresh] = useState(0);
  const invoiceFileRef = useRef<HTMLInputElement>(null);

  const fetchInvoices = () => {
    axios.get('/api/invoices')
      .then((res) => setInvoices(res.data.invoices || []))
      .catch(() => setInvoices([]));
  };

  useEffect(() => { fetchInvoices(); }, []);

  const handleInvoiceUpload = async (file: File) => {
    const ok = ['.pdf', '.png', '.jpg', '.jpeg', '.webp', '.txt'];
    if (!ok.some((ext) => file.name.toLowerCase().endsWith(ext))) {
      toast.error('Invoices can be a PDF, a photo (PNG/JPG/WebP), or a text file');
      return;
    }
    if (file.size > 10_000_000) {
      toast.error('File too large (max 10MB)');
      return;
    }
    setInvoiceUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post('/api/invoices/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const s = res.data;
      if (s.status === 'extraction_failed') {
        toast.error("We couldn't read that invoice cleanly — try a clearer photo or the original PDF.");
      } else if (!s.checksum_ok) {
        toast.warning(`Read ${s.lines} lines, but they don't add up to the printed total — held for review, nothing committed.`);
      } else {
        toast.success(`Read ${s.lines} lines — ${s.lines - s.needs_owner} handled automatically${s.needs_owner ? `, ${s.needs_owner} need a quick look below` : ''}`);
      }
      pageCache.clear('/api/'); // costs may have changed
      fetchInvoices();
      setInboxRefresh((n) => n + 1);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Invoice upload failed');
    } finally {
      setInvoiceUploading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <h2 className="font-serif text-xl text-[#2D2A2E]">Supplier Invoices</h2>
        <span className="inline-block bg-[#4A2D5E] text-white text-[10px] font-sans font-medium rounded-full px-2 py-0.5 uppercase tracking-wide">New</span>
      </div>
      <p className="font-sans text-sm text-[#6B6670] mb-2 max-w-2xl">
        Add what your suppliers charge you and your margin numbers become real, not estimates.
        ClearPath reads the invoice, checks that the lines add up to the printed total, and only
        asks you about items it isn't sure of — once, ever.
      </p>
      <p className="font-sans text-sm text-[#6B6670] mb-4 max-w-2xl">
        You can also forward invoices to your ClearPath address (Settings → Data).
      </p>
      {/* The drop zone is the main control on the Costs door, so it is a
          keyboard control too: focusable, named, Enter/Space open the picker.
          A div with role="button" rather than a <button>, which may not hold
          the heading inside it. Its name starts with the heading's words
          (WCAG 2.5.3), so saying what is on screen reaches it. */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Drop a supplier invoice here, or choose a file"
        aria-busy={invoiceUploading}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            invoiceFileRef.current?.click();
          }
        }}
        onDragOver={(e) => { e.preventDefault(); setInvoiceDragOver(true); }}
        onDragLeave={() => setInvoiceDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setInvoiceDragOver(false);
          const file = e.dataTransfer.files[0];
          if (file) handleInvoiceUpload(file);
        }}
        onClick={() => invoiceFileRef.current?.click()}
        className={`bg-white border-2 border-dashed rounded-[14px] p-10 text-center cursor-pointer transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4A2D5E] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FAF9F7] ${
          invoiceDragOver ? 'border-[#4A2D5E] bg-[#F0EBF5]' : 'border-[#E6E2DE] hover:border-[#4A2D5E]/40'
        }`}
      >
        <input
          ref={invoiceFileRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.txt"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleInvoiceUpload(file);
            e.target.value = '';
          }}
          className="hidden"
        />
        {invoiceUploading ? (
          <LoadingScreen fullScreen={false} compact label="Reading your invoice…" />
        ) : (
          <>
            <Receipt size={36} strokeWidth={1.5} className="text-[#4A2D5E]/40 mx-auto mb-3" />
            <h3 className="font-serif text-lg text-[#2D2A2E] mb-1">Drop a supplier invoice here</h3>
            <p className="font-sans text-sm text-[#6B6670]">PDF, photo, or text file — max 10MB</p>
          </>
        )}
      </div>

      <div className="mt-6">
        <InvoiceInbox key={inboxRefresh} variant="upload" />
      </div>

      {invoices.length > 0 && (
        <div className="mt-6 space-y-3">
          <h3 className="font-serif text-lg text-[#2D2A2E]">Invoice History</h3>
          {invoices.slice(0, 10).map((inv) => {
            const status = INVOICE_STATUS[inv.status] || { label: inv.status, cls: 'bg-[#FAF9F7] text-[#6B6670]' };
            return (
              <div key={inv.invoice_id} className="bg-white border border-[#E6E2DE] rounded-[14px] p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#4A2D5E]/10 flex items-center justify-center flex-shrink-0">
                  <FileText size={18} strokeWidth={1.5} className="text-[#4A2D5E]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-sans text-sm font-medium text-[#2D2A2E] truncate">
                    {inv.supplier_name || inv.filename}
                  </div>
                  <div className="font-sans text-xs text-[#6B6670]">
                    {inv.invoice_date || '—'}{typeof inv.total_due === 'number' ? ` · ${fmtCents(inv.total_due)}` : ''}
                    {inv.source?.kind === 'email' && (
                      <span className="ml-2 inline-block rounded-full bg-[#4A2D5E]/10 text-[#4A2D5E] px-2 py-0.5">
                        via email{inv.source.from ? ` · ${inv.source.from}` : ''}
                      </span>
                    )}
                  </div>
                </div>
                <span
                  title={inv.error || undefined}
                  className={`inline-block rounded-full px-2.5 py-1 font-sans text-xs font-medium flex-shrink-0 ${status.cls}`}
                >
                  {status.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
